import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { fmtDate, timeAgo } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Handshake, Building2, Mail, Phone, Link2, FileText, Loader2, CheckCircle2, XCircle, HelpCircle, ArrowRightLeft } from "lucide-react";

const STATE_STYLE = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  info_requested: "bg-sky-100 text-sky-800 border-sky-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
  converted: "bg-[#255058]/10 text-[#255058] border-[#255058]/25",
};
const STATE_LABEL = { pending: "Pending review", accepted: "Accepted", info_requested: "Info requested", rejected: "Rejected", converted: "Converted" };

export default function HandoffReview() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/sales/handoffs"); setData(data); }
    catch (e) { toast.error(apiErr(e)); setData({ items: [], counts: {} }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="space-y-3"><Skeleton className="h-20" /><Skeleton className="h-40" /></div>;
  const items = data.items.filter((i) => filter === "all" || (i.review?.status || "pending") === filter);

  return (
    <div className="space-y-4" data-testid="handoff-review-panel">
      <div className="flex flex-wrap gap-2">
        {[["all", `All (${data.items.length})`], ...Object.entries(STATE_LABEL).map(([k, l]) => [k, `${l} (${data.counts?.[k] ?? 0})`])].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`handoff-filter-${k}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${filter === k ? "border-[#255058] bg-[#255058] text-white" : "border-border text-muted-foreground hover:border-[#255058]/40"}`}>
            {l}
          </button>
        ))}
      </div>

      {!items.length ? (
        <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="handoff-empty">
          <Handshake className="mx-auto mb-3 h-6 w-6 text-[#255058]/60" />
          No recruitment opportunities handed over yet.
        </Card>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {items.map((h) => {
            const st = h.review?.status || "pending";
            return (
              <Card key={h.handoff_id} className="p-4 transition-all hover:border-[#255058]/40 hover:shadow-md" data-testid={`handoff-card-${h.handoff_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 shrink-0 text-[#255058]" />
                      <span className="truncate font-display font-semibold">{h.company}</span>
                    </div>
                    <div className="mt-1 truncate text-xs text-muted-foreground">
                      {h.position_title || "Requirement not specified"} · {h.location || h.region || "—"}
                    </div>
                  </div>
                  <Badge variant="outline" className={`shrink-0 text-[10px] ${STATE_STYLE[st]}`}>{STATE_LABEL[st]}</Badge>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                  <span>{(h.pocs || []).length} POC{(h.pocs || []).length === 1 ? "" : "s"}</span>
                  <span>Handed over {timeAgo(h.handed_off_at)}</span>
                  {h.task?.due_at && <span>Due {fmtDate(h.task.due_at)}</span>}
                </div>
                <Button size="sm" variant="outline" className="mt-3 w-full" data-testid={`handoff-open-${h.handoff_id}`}
                  onClick={() => setActive(h)}>Open review</Button>
              </Card>
            );
          })}
        </div>
      )}

      {active && <HandoffDialog handoff={active} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }} />}
    </div>
  );
}

function HandoffDialog({ handoff, onClose, onDone }) {
  const [h, setH] = useState(handoff);
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const [clientMode, setClientMode] = useState("new");
  const [clientId, setClientId] = useState("");
  const [job, setJob] = useState({ create_job: false, job_title: h.position_title || "", job_openings: 1, job_location: h.location || "", job_description: "" });
  const st = h.review?.status || "pending";
  const locked = !h.can_manage || st === "converted";

  const refresh = async () => { const { data } = await api.get(`/sales/handoffs/${h.handoff_id}`); setH(data); };

  const decide = async (action) => {
    if (action === "reject" && !reason.trim()) { toast.error("A reason is required to reject"); return; }
    if (action === "request_info" && !reason.trim()) { toast.error("Describe what you need from the R.A. team"); return; }
    setBusy(true);
    try {
      await api.post(`/sales/handoffs/${h.handoff_id}/decision`, { action, reason: action === "reject" ? reason : null, note: action === "request_info" ? reason : null });
      toast.success(action === "accept" ? "Opportunity accepted" : action === "reject" ? "Opportunity rejected" : "Request sent to R.A. Admin");
      setReason(""); setMode(null); await refresh(); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const openConvert = async () => {
    setMode("convert");
    try { const { data } = await api.get(`/sales/handoffs/${h.handoff_id}/duplicate-clients`); setDup(data); if (data.matches?.length) setClientMode("existing"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const convert = async () => {
    if (!dup?.linked_client && clientMode === "existing" && !clientId) { toast.error("Choose an existing client"); return; }
    if (job.create_job && !job.job_title.trim()) { toast.error("Job title is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/sales/handoffs/${h.handoff_id}/convert`, {
        client_mode: clientMode, client_id: clientMode === "existing" ? clientId : null,
        create_job: job.create_job, job_title: job.job_title, job_openings: Number(job.job_openings) || 1,
        job_location: job.job_location, job_description: job.job_description });
      toast.success(data.client_reused ? "Linked to existing client" : "Client created");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-3xl overflow-y-auto scrollbar-thin" data-testid="handoff-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{h.company}</DialogTitle>
          <DialogDescription>R.A. sourcing details are read-only. Your decision is stored on the BDM opportunity record.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={`text-[10px] ${STATE_STYLE[st]}`}>{STATE_LABEL[st]}</Badge>
          {h.task?.due_at && <span className="text-xs text-muted-foreground">Review due {fmtDate(h.task.due_at)}</span>}
        </div>

        <div className="mt-2 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          <Row k="Company" v={h.company} /><Row k="Industry" v={h.industry} />
          <Row k="Location" v={h.location} /><Row k="Region" v={h.region} />
          <Row k="Requirement" v={h.position_title} /><Row k="Source" v={h.source} />
          <Row k="R.A. Owner" v={h.ra_owner} /><Row k="Handed over" v={fmtDate(h.handed_off_at)} />
          <Row k="Job posting" v={h.job_posting_url} /><Row k="Existing client" v={h.client_name} />
        </div>

        <Section title="Points of contact">
          <div className="grid gap-2 sm:grid-cols-2">
            {(h.pocs || []).map((p, i) => (
              <div key={i} className="rounded-lg border border-border p-3 text-sm" data-testid={`handoff-poc-${i}`}>
                <div className="font-medium">{p.name} {p.is_primary && <Badge variant="outline" className="ml-1 text-[9px]">Primary</Badge>}</div>
                <div className="text-xs text-muted-foreground">{p.title || "—"}</div>
                {p.email && <div className="mt-1 flex items-center gap-1.5 text-xs"><Mail className="h-3 w-3" />{p.email}</div>}
                {p.phone && <div className="flex items-center gap-1.5 text-xs"><Phone className="h-3 w-3" />{p.phone}</div>}
                {p.linkedin && <div className="flex items-center gap-1.5 text-xs"><Link2 className="h-3 w-3" />{p.linkedin}</div>}
              </div>
            ))}
            {!(h.pocs || []).length && <div className="text-sm text-muted-foreground">No POC shared.</div>}
          </div>
        </Section>

        <Section title="R.A. notes">
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{h.ra_notes || "—"}</p>
        </Section>
        <Section title="Client response">
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{h.client_response || "—"}</p>
          {h.client_response_at && <div className="mt-1 text-[11px] text-muted-foreground">{fmtDate(h.client_response_at)}</div>}
        </Section>
        <Section title="Evidence">
          {(h.evidence || []).length ? (h.evidence || []).map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-sm"><FileText className="h-3.5 w-3.5 text-[#255058]" />{e.name}<span className="text-xs text-muted-foreground">· {e.category}</span></div>
          )) : <div className="text-sm text-muted-foreground">No evidence attached.</div>}
        </Section>

        {(h.review?.decisions || []).length > 0 && (
          <Section title="Decision history">
            <div className="space-y-2">
              {h.review.decisions.map((d, i) => (
                <div key={i} className="rounded-lg border border-border p-2.5 text-xs">
                  <span className="font-medium">{STATE_LABEL[d.status] || d.status}</span> · {d.by_name} · {fmtDate(d.at)}
                  {(d.reason || d.note) && <div className="mt-1 text-muted-foreground">{d.reason || d.note}</div>}
                </div>
              ))}
            </div>
          </Section>
        )}

        {!locked && (
          <div className="mt-4 space-y-3 border-t border-border pt-4">
            {mode === "convert" ? (
              <div className="space-y-3">
                {dup?.linked_client ? (
                  <div className="rounded-lg border border-[#255058]/30 bg-[#255058]/5 p-3 text-sm">
                    Already linked to <b>{dup.linked_client.company_name}</b> — this client will be reused.
                  </div>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Client</Label>
                      <Select value={clientMode} onValueChange={setClientMode}>
                        <SelectTrigger data-testid="handoff-client-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Create new client</SelectItem>
                          <SelectItem value="existing">Use existing client</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {clientMode === "existing" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Possible duplicates</Label>
                        <Select value={clientId} onValueChange={setClientId}>
                          <SelectTrigger data-testid="handoff-existing-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                          <SelectContent>{(dup?.matches || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.company_name} · {m.region}</SelectItem>)}</SelectContent>
                        </Select>
                        {!(dup?.matches || []).length && <div className="text-xs text-muted-foreground">No similar client found — create a new one instead.</div>}
                      </div>
                    )}
                  </>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="handoff-create-job" checked={job.create_job} onChange={(e) => setJob({ ...job, create_job: e.target.checked })} />
                  Also create a job / requisition (saved as Draft)
                </label>
                {job.create_job && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5"><Label className="text-xs">Job title *</Label><Input data-testid="handoff-job-title" value={job.job_title} onChange={(e) => setJob({ ...job, job_title: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Openings</Label><Input type="number" value={job.job_openings} onChange={(e) => setJob({ ...job, job_openings: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Location</Label><Input value={job.job_location} onChange={(e) => setJob({ ...job, job_location: e.target.value })} /></div>
                    <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Description</Label><Textarea rows={3} value={job.job_description} onChange={(e) => setJob({ ...job, job_description: e.target.value })} /></div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setMode(null)}>Back</Button>
                  <Button className="flex-1 bg-[#255058] text-white hover:bg-[#1c3d43]" disabled={busy} onClick={convert} data-testid="handoff-convert-confirm">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Convert"}
                  </Button>
                </div>
              </div>
            ) : mode ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{mode === "reject" ? "Rejection reason *" : "What do you need from the R.A. team? *"}</Label>
                  <Textarea rows={3} data-testid="handoff-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setMode(null); setReason(""); }}>Cancel</Button>
                  <Button className="flex-1 bg-[#255058] text-white hover:bg-[#1c3d43]" disabled={busy} onClick={() => decide(mode)} data-testid="handoff-reason-submit">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-4">
                <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => decide("accept")} data-testid="handoff-accept">
                  <CheckCircle2 className="h-4 w-4" /> Accept
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setMode("request_info")} data-testid="handoff-request-info">
                  <HelpCircle className="h-4 w-4" /> Request info
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5 text-[#c65a63]" onClick={() => setMode("reject")} data-testid="handoff-reject">
                  <XCircle className="h-4 w-4" /> Reject
                </Button>
                <Button size="sm" className="gap-1.5 bg-[#255058] text-white hover:bg-[#1c3d43]" onClick={openConvert} data-testid="handoff-convert">
                  <ArrowRightLeft className="h-4 w-4" /> Convert
                </Button>
              </div>
            )}
          </div>
        )}
        {locked && <div className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">{st === "converted" ? "This opportunity has been converted." : "Read-only access."}</div>}
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) { return <div className="flex justify-between gap-4"><span className="text-muted-foreground">{k}</span><span className="text-right font-medium">{v || "—"}</span></div>; }
function Section({ title, children }) {
  return <div className="mt-4"><div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#255058]">{title}</div>{children}</div>;
}
